/**
 * The messages between the page and the image worker (decision 56; part 16a log, table M): the page sends one file's
 * bytes and what the image is for, the worker answers with the image made or why there is none; or (part 23b, table PX)
 * the page sends a re-imported picture's bytes and the stored image's, and the worker says whether they are the same
 * picture. Each side reads what it receives field by field (Definition of Done, security), so a message that isn't one
 * of these is never taken for one.
 */
import type { ImageFormat, ImagePurpose } from '@cockatrice/core';
import type { EncodeRefusal } from './encode.ts';

/** M1: one file to make into an image. */
export interface ImageJob {
  readonly kind: 'encode';
  readonly bytes: ArrayBuffer;
  readonly purpose: ImagePurpose;
}

/** Part 23b, PX: a re-imported picture's bytes and the stored image's, to compare. */
export interface CompareJob {
  readonly kind: 'compare';
  readonly bytes: ArrayBuffer;
  readonly stored: ArrayBuffer;
}

/** M2, M3: the image made, or why not; PX: whether the two compared are the same picture. */
export type ImageReply =
  | { readonly kind: 'encoded'; readonly bytes: ArrayBuffer; readonly format: ImageFormat; readonly width: number; readonly height: number }
  | { readonly kind: 'refused'; readonly reason: EncodeRefusal }
  | { readonly kind: 'compared'; readonly same: boolean };

const PURPOSES: readonly ImagePurpose[] = ['text', 'cover'];
const FORMATS: readonly ImageFormat[] = ['jpeg', 'png'];
const REASONS: readonly EncodeRefusal[] = ['unsupported', 'damaged', 'too-large', 'too-big', 'failed'];

/**
 * The own fields of a plain object with exactly `count` fields, or null. Every field a message has is then read by name and
 * checked to be a value of its kind, which an absent one isn't, so the count is what leaves no room for another field.
 */
function fieldsOf(value: unknown, count: number): ReadonlyMap<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const fields = new Map(Object.entries(value));
  return fields.size === count ? fields : null;
}

/** Whether `value` is a whole number from 1. */
function isSide(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) >= 1;
}

/** M1, PX: the job a message asks for, or null for anything else (M4). */
export function readJob(value: unknown): ImageJob | CompareJob | null {
  const fields = fieldsOf(value, 3);
  if (fields?.get('kind') === 'compare') {
    const bytes = fields.get('bytes');
    const stored = fields.get('stored');
    return bytes instanceof ArrayBuffer && stored instanceof ArrayBuffer ? { kind: 'compare', bytes, stored } : null;
  }
  const bytes = fields?.get('bytes');
  const purpose = PURPOSES.find((known) => known === fields?.get('purpose'));
  return fields?.get('kind') === 'encode' && bytes instanceof ArrayBuffer && purpose !== undefined ? { kind: 'encode', bytes, purpose } : null;
}

/** M2, M3: the reply a message is, or null for anything else (M4). */
export function readReply(value: unknown): ImageReply | null {
  const kind: unknown = typeof value === 'object' && value !== null ? Reflect.get(value, 'kind') : undefined;
  if (kind === 'encoded') {
    const fields = fieldsOf(value, 5);
    const bytes = fields?.get('bytes');
    const format = FORMATS.find((known) => known === fields?.get('format'));
    const width = fields?.get('width');
    const height = fields?.get('height');
    return bytes instanceof ArrayBuffer && format !== undefined && isSide(width) && isSide(height) ? { kind, bytes, format, width, height } : null;
  }
  if (kind === 'refused') {
    const reason = REASONS.find((known) => known === fieldsOf(value, 2)?.get('reason'));
    return reason === undefined ? null : { kind, reason };
  }
  if (kind === 'compared') {
    const same = fieldsOf(value, 2)?.get('same');
    return typeof same === 'boolean' ? { kind, same } : null;
  }
  return null;
}
